import { View } from "react-native-web";
import CustomText from "../../components/atoms/CustomText";
import { spacing, colors } from "../../utils/styling";

import { useResponsive } from "../../hooks/useResponsive";

import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getResultsDataFromStateOrStorage } from "../../utils/resultsData";
import { emailResultsPdf } from "../../lib/resultsEmail";
import { resolveCoreSection } from "../../utils/quizinfo/personalityContent";

// ResultsData type is provided by utils/resultsData

const CATEGORY_COLORS: Record<string, string> = {
  "Dream Chaser": "#00A4EF",
  "Wealth Architect": "#00B3C2",
  "Safety Seeker": "#076652",
  "Generous Altruist": "#9933FF",
  "Freedom Finder": "#FF00FF",
};

const SPECIFIC_CATEGORY_COLORS: Record<string, string> = {
  "Visionary Investor": "#00CFEF",
  "Powerhouse Investor": "#0074EF",
  "Cautious Saver": "#77FF92",
  "Responsible Saver": "#00AE23",
  "Analytic Investor": "#7BCCD2",
  "Ambitious Builder": "#00757F",
  "Purpose-Driven Provider": "#BB77FF",
  "Empathetic Giver": "#6510B9",
  "Lifestyle Spender": "#FF8AFF",
  "Spontaneous Spender": "#D600D6",
};

// Mapping from main categories to their specific sub-types
const MAIN_TO_SPECIFIC_MAPPING: Record<string, string[]> = {
  "Dream Chaser": ["Visionary Investor", "Powerhouse Investor"],
  "Safety Seeker": ["Cautious Saver", "Responsible Saver"],
  "Wealth Architect": ["Analytic Investor", "Ambitious Builder"],
  "Generous Altruist": ["Purpose-Driven Provider", "Empathetic Giver"],
  "Freedom Finder": ["Lifestyle Spender", "Spontaneous Spender"],
};

export default function ResultsReady() {
  const { isMobile } = useResponsive();
  const location = useLocation();
  const navigate = useNavigate();

  // Unified: prefer location.state, else localStorage, with migration
  let resultsData = getResultsDataFromStateOrStorage(location.state);

  const defaultResultsData = {
    answers: [],
    specificAnswers: [],
    categoryScores: {},
    specificCategoryScores: {},
    dominantCategory: "",
    specificDominantCategory: "",
    sfAccount: null,
    quizId: undefined,
    leadId: undefined,
    email: undefined,
    firstName: "",
    lastName: "",
    phone: "",
  };

  // Use resultsData if available, otherwise use defaults
  const safeResultsData = resultsData || (defaultResultsData as any);

  const {
    dominantCategory,
    categoryScores,
    sfAccount,
  } = safeResultsData;

  const revealSection = resolveCoreSection(dominantCategory, "ResultsReady");
  const topThree: Array<[string, number]> = Object.entries(
    (categoryScores || {}) as Record<string, number>
  )
    .filter(([, pct]) => typeof pct === "number" && pct > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  // Create the gradient colors for the border
  const baseColor1 = SPECIFIC_CATEGORY_COLORS[
    MAIN_TO_SPECIFIC_MAPPING[dominantCategory]?.[0] || ""
  ];
  const baseColor2 = SPECIFIC_CATEGORY_COLORS[
    MAIN_TO_SPECIFIC_MAPPING[dominantCategory]?.[1] || ""
  ];
  
  // Create more contrast by making lighter colors lighter and darker colors darker
  const adjustColor = (color?: string) => {
    const fallback = '#888888';
    // Validate input is a full 6-char hex string like #RRGGBB
    if (typeof color !== 'string' || !/^#[0-9A-Fa-f]{6}$/.test(color)) {
      return fallback;
    }

    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);

    // Calculate brightness
    const brightness = (r + g + b) / 3;

    let newR, newG, newB;
    if (brightness > 128) {
      // Light color - make it lighter
      newR = Math.min(255, Math.floor(r * 1.3));
      newG = Math.min(255, Math.floor(g * 1.3));
      newB = Math.min(255, Math.floor(b * 1.3));
    } else {
      // Dark color - make it darker
      newR = Math.max(0, Math.floor(r * 0.7));
      newG = Math.max(0, Math.floor(g * 0.7));
      newB = Math.max(0, Math.floor(b * 0.7));
    }

    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  };
  
  const gradientColor1 = adjustColor(baseColor1);
  const gradientColor2 = adjustColor(baseColor2);

  // Email the results PDF as soon as results are shown — every completion, including
  // retakes. Runs in the background; claimResultsEmail() keys on quizId so a refresh or
  // a re-render does not send twice. (2026-09-23 team call)
  useEffect(() => {
    emailResultsPdf(safeResultsData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeResultsData?.quizId, safeResultsData?.email]);

  const handleContinue = () => {
    // Straight to profile creation. The old in-between "Get my free results now" screen
    // (/quiz/unlock) is skipped: McKayla + Nina found the double step too much (9/23).
    navigate('/signup', {
      state: { ...safeResultsData, paymentConfirmed: true, from: location.pathname }
    })
  }

  return (
    <View
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: isMobile ? spacing.md : spacing.xl,
        paddingLeft: isMobile ? spacing.lg : spacing.xl,
        paddingRight: isMobile ? spacing.lg : spacing.xl,
        marginBottom: spacing.xxl,
      }}
    >
      <style>
        {`
          .circle-border {
            border-radius: 12px;
            background: linear-gradient(#000000, #000000) padding-box, linear-gradient(
                  var(--angle),
                  ${gradientColor1},
                  ${gradientColor2}
                ) border-box;
            animation: 4s rotate-border linear infinite;
            border: 3px solid transparent;
            box-sizing: border-box;
            width: calc(100% - 2 * ${isMobile ? spacing.md : spacing.lg});
            margin: 0 auto;
          }
          
          button.circle-border {
            border-radius: 50px;
            background: linear-gradient(${gradientColor1}, ${gradientColor2}) padding-box, linear-gradient(
                  var(--angle),
                  ${gradientColor1},
                  ${gradientColor2}
                ) border-box;
            animation: 4s rotate-border linear infinite;
            border: 3px solid transparent;
            box-sizing: border-box;
            width: auto;
            margin: 0;
            animation: 4s rotate-glow linear infinite;
          }
          
          @keyframes rotate-glow {
            0% {
              box-shadow: 0 0 20px ${gradientColor1}40, 0 0 40px ${gradientColor2}20;
            }
            25% {
              box-shadow: 0 0 20px ${gradientColor2}40, 0 0 40px ${gradientColor1}20;
            }
            50% {
              box-shadow: 0 0 20px ${gradientColor1}40, 0 0 40px ${gradientColor2}20;
            }
            75% {
              box-shadow: 0 0 20px ${gradientColor2}40, 0 0 40px ${gradientColor1}20;
            }
            100% {
              box-shadow: 0 0 20px ${gradientColor1}40, 0 0 40px ${gradientColor2}20;
            }
          }

          @keyframes rotate-border {
            to {
              --angle: 360deg;
            }
          }

          @property --angle {
            syntax: "<angle>";
            initial-value: 0deg;
            inherits: false;
          }
        `}
      </style>
      <View
        style={{
          width: "100%",
          maxWidth: 1400,
          paddingTop: isMobile ? spacing.lg : spacing.lg * 1.25,
          paddingBottom: isMobile ? spacing.xxl : 0,
        }}
      >
        {/* Card for heading and subtitle */}
        <View
          style={{
            backgroundColor: colors.white,
            borderRadius: 16,
            padding: isMobile ? spacing.md : spacing.xl,
            marginBottom: spacing.xl,
            width: "100%",
            alignItems: "center",
            border: "3px solid black",
          }}
        >
          <CustomText
            style={{
              color: colors.dark,
              textAlign: "center",
              width: "100%",
              fontFamily: "Nunito",
              fontWeight: 1000,
              fontSize: isMobile ? 38 : 80,
              lineHeight: "1.1",
            }}
          >
            Congratulations
          </CustomText>
          <CustomText
            style={{
              color: colors.dark,
              textAlign: "center",
              width: "100%",
              fontFamily: "Nunito",
              fontWeight: 600,
              fontSize: isMobile ? 30 : 65,
              lineHeight: "1.1",
              marginBottom: isMobile ? spacing.md : spacing.lg,
            }}
          >
            your custom plan is ready!
          </CustomText>
          <CustomText
            style={{
              color: colors.dark,
              textAlign: "center",
              width: "100%",
              fontFamily: "Nunito",
              fontWeight: 400,
              fontSize: isMobile ? 20 : 24,
              marginBottom: isMobile ? spacing.xl : spacing.xxl,
              lineHeight: "1.1",
            }}
          >
            <i>Based on your answers and studies of behavioral finance.</i>
          </CustomText>
          {/* Results */}
          <div
            className=""
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              padding: isMobile ? spacing.md : spacing.lg,
            }}
          >
            <CustomText
              style={{
                textAlign: "center",
                width: "100%",
                fontFamily: "Nunito",
                fontWeight: 700,
                fontSize: isMobile ? 30 : 65,
                color: colors.dark,
              }}
            >
              You are primarily a:
              {dominantCategory.toLowerCase().startsWith("a") ||
              dominantCategory.toLowerCase().startsWith("e") ||
              dominantCategory.toLowerCase().startsWith("i") ||
              dominantCategory.toLowerCase().startsWith("o") ||
              dominantCategory.toLowerCase().startsWith("u")
                ? "n "
                : ""}
            </CustomText>
            <CustomText
              style={{
                fontSize: isMobile ? 38 : 80,
                color: CATEGORY_COLORS[dominantCategory],
                fontWeight: "700",
                textAlign: "center",
                width: "100%",
                fontFamily: "Nunito",
              }}
            >
              {dominantCategory}
            </CustomText>
            {/* McKayla 2026-10-12: the reveal shows the primary personality, the top
                three scores and a short description of what they mean. This replaced
                a "Your money personality is: <sub-type>" line that has rendered blank
                since sub-types were retired on 2026-08-20. */}
            {revealSection.Subtext ? (
              <CustomText
                style={{
                  fontSize: isMobile ? 20 : 30,
                  color: colors.gray[700],
                  textAlign: "center",
                  width: "100%",
                  fontFamily: "Nunito",
                  fontWeight: 700,
                  marginTop: spacing.sm,
                }}
              >
                {revealSection.Subtext}
              </CustomText>
            ) : null}
            {revealSection.Overview ? (
              <CustomText
                style={{
                  fontSize: isMobile ? 16 : 20,
                  color: colors.gray[700],
                  textAlign: "center",
                  maxWidth: 760,
                  fontFamily: "Nunito",
                  lineHeight: isMobile ? "24px" : "30px",
                  marginTop: spacing.md,
                }}
              >
                {revealSection.Overview}
              </CustomText>
            ) : null}
            {topThree.length > 0 && (
              <View
                style={{
                  width: "100%",
                  maxWidth: 560,
                  marginTop: isMobile ? spacing.lg : spacing.xl,
                  marginBottom: isMobile ? spacing.lg * 2 : spacing.xl * 2,
                }}
              >
                <CustomText
                  style={{
                    fontSize: isMobile ? 18 : 22,
                    fontWeight: "700",
                    color: colors.dark,
                    fontFamily: "Nunito",
                    textAlign: "center",
                    marginBottom: spacing.md,
                  }}
                >
                  Your top three
                </CustomText>
                {topThree.map(([name, pct]) => (
                  <View key={name} style={{ marginBottom: spacing.sm }}>
                    <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 4 }}>
                      <CustomText style={{ fontSize: isMobile ? 16 : 18, fontWeight: "700", color: colors.dark, fontFamily: "Nunito" }}>
                        {name}
                      </CustomText>
                      <CustomText style={{ fontSize: isMobile ? 16 : 18, fontWeight: "700", color: colors.gray[700], fontFamily: "Nunito" }}>
                        {Math.round(pct)}%
                      </CustomText>
                    </View>
                    <View style={{ height: 12, backgroundColor: colors.gray[200], borderRadius: 999, overflow: "hidden" }}>
                      <View
                        style={{
                          height: "100%",
                          width: `${Math.max(0, Math.min(100, pct))}%`,
                          backgroundColor: CATEGORY_COLORS[name] || colors.primary,
                          borderRadius: 999,
                        }}
                      />
                    </View>
                  </View>
                ))}
              </View>
            )}
            <button
              className="circle-border"
              onClick={handleContinue}
              style={{
                color: "white",
                borderRadius: 50,
                padding: isMobile ? `${spacing.md}px ${spacing.xl}px` : `${spacing.md}px ${spacing.xl * 2}px`,
                fontFamily: "Nunito",
                fontSize: isMobile ? 14 : 28,
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.3s ease",
                minWidth: 200,
                marginBottom: isMobile ? spacing.md : spacing.lg,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.opacity = "0.75";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.opacity = "1";
              }}
            >
              Get to Know Your Money Personality
            </button>
          </div>

          {sfAccount && (
            <View
              style={{
                backgroundColor: colors.green + "20",
                padding: isMobile ? spacing.xs : spacing.sm,
                paddingLeft: isMobile ? spacing.md : spacing.lg,
                paddingRight: isMobile ? spacing.md : spacing.lg,
                borderRadius: 8,
                marginTop: isMobile ? 8 : 8,
                alignSelf: "center",
              }}
            >
              <CustomText
                style={{
                  color: colors.green,
                  textAlign: "center",
                  whiteSpace: isMobile ? "normal" : "nowrap",
                  fontSize: isMobile ? 18 : 24,
                  fontFamily: "Nunito",
                }}
              >
                Your advisor has received your results!
              </CustomText>
            </View>
          )}
        </View>
      </View>
    </View>
  );
}
